import { Workflow, Building2, CheckCircle2, XCircle, AlertCircle, Clock, LinkIcon, Eye, EyeOff } from 'lucide-react'
import { useState, useEffect } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  registerAndAcceptInvite,
  loginAndAcceptInvite,
} from '@/services/api/auth.service'
import { ApiError } from '@/services/api/client'
import {
  getInviteInfo,
  acceptInvite,
  rejectInvite,
} from '@/services/api/workspace.service'
import { useAuthStore } from '@/stores/auth.store'
import { useWorkspaceStore } from '@/stores/workspace.store'
import type { AcceptInviteAuthResponse } from '@/types/api/auth'
import type { InviteInfoResponse } from '@/types/api/workspace'


// ─── State machine types ─────────────────────────────────────────────────────

type PageState =
  | 'loading'
  | 'invalid'
  | 'already_accepted'
  | 'already_rejected'
  | 'expired'
  | 'needs_login'
  | 'email_mismatch'
  | 'ready'
  | 'action_loading'
  | 'accepted'
  | 'declined'

type LoginTab = 'signin' | 'register'

// ─── Shared page wrapper ─────────────────────────────────────────────────────

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-neutral-50 px-4">
      <div className="w-full max-w-md space-y-6">
        {/* Branding */}
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary-600">
            <Workflow className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-2xl font-bold">Generic WorkFlow</h1>
        </div>
        {children}
      </div>
    </div>
  )
}

// ─── Static state screens ─────────────────────────────────────────────────────

function StatusScreen({
  icon,
  iconClass,
  title,
  description,
  action,
}: {
  icon: React.ReactNode
  iconClass: string
  title: string
  description: string
  action?: React.ReactNode
}) {
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-4 py-8 text-center">
        <div className={`rounded-full p-3 ${iconClass}`}>{icon}</div>
        <div>
          <p className="text-lg font-semibold text-neutral-900">{title}</p>
          <p className="mt-1 text-sm text-neutral-500">{description}</p>
        </div>
        {action}
      </CardContent>
    </Card>
  )
}

// ─── Accept / Decline dialog card ────────────────────────────────────────────

function AcceptDeclineCard({
  inviteInfo,
  onAccepted,
  onDeclined,
  isLoading,
}: {
  inviteInfo: InviteInfoResponse
  token: string
  onAccepted: () => void
  onDeclined: () => void
  isLoading: boolean
}) {
  return (
    <Card>
      <CardHeader className="space-y-1 pb-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100">
            <Building2 className="h-5 w-5 text-primary-600" />
          </div>
          <div>
            <CardTitle className="text-lg">{inviteInfo.workspaceName}</CardTitle>
            <CardDescription>Workspace invitation</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-neutral-600">
          You've been invited to join{' '}
          <span className="font-semibold text-neutral-900">{inviteInfo.workspaceName}</span>.
          Would you like to accept?
        </p>

        <div className="flex gap-3">
          <Button className="flex-1" onClick={onAccepted} disabled={isLoading}>
            {isLoading ? 'Processing…' : 'Accept'}
          </Button>
          <Button variant="outline" className="flex-1" onClick={onDeclined} disabled={isLoading}>
            Decline
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Email mismatch warning card ──────────────────────────────────────────────

function MismatchCard({
  inviteInfo,
  loggedInEmail,
}: {
  inviteInfo: InviteInfoResponse
  loggedInEmail: string
}) {
  const navigate = useNavigate()
  const { logout } = useAuthStore()

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <Card className="border-warning-200 bg-warning-50">
      <CardContent className="flex flex-col gap-4 py-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-warning-600" />
          <div>
            <p className="font-semibold text-neutral-900">Wrong account</p>
            <p className="mt-1 text-sm text-neutral-600">
              You're signed in as{' '}
              <span className="font-medium">{loggedInEmail}</span>, but this
              invite is for{' '}
              <span className="font-medium">{inviteInfo.email}</span>.
            </p>
            <p className="mt-1 text-sm text-neutral-500">
              Please log out and sign in with the correct account to accept.
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={handleLogout} className="w-full">
          Log out and switch account
        </Button>
      </CardContent>
    </Card>
  )
}

// ─── Invite auth card (sign in + create account tabs) ────────────────────────

function InviteAuthCard({
  inviteInfo,
  token,
  defaultTab,
  onSuccess,
}: {
  inviteInfo: InviteInfoResponse
  token: string
  defaultTab?: LoginTab
  onSuccess: (response: AcceptInviteAuthResponse) => void
}) {
  const [activeTab, setActiveTab] = useState<LoginTab>(defaultTab ?? 'signin')

  // ── Sign-in tab state ──────────────────────────────────────────────────────
  const [signinPassword, setSigninPassword] = useState('')
  const [signinShowPw, setSigninShowPw] = useState(false)
  const [signinError, setSigninError] = useState<string | null>(null)
  const [signinLoading, setSigninLoading] = useState(false)

  // ── Register tab state ─────────────────────────────────────────────────────
  const [fullName, setFullName] = useState('')
  const [registerPassword, setRegisterPassword] = useState('')
  const [registerShowPw, setRegisterShowPw] = useState(false)
  const [registerError, setRegisterError] = useState<string | null>(null)
  const [registerLoading, setRegisterLoading] = useState(false)

  // Switch tab and clear errors
  const switchTab = (tab: LoginTab) => {
    setActiveTab(tab)
    setSigninError(null)
    setRegisterError(null)
  }

  // ── Handlers ───────────────────────────────────────────────────────────────

  const handleSignin = async (e: React.FormEvent) => {
    e.preventDefault()
    setSigninError(null)
    setSigninLoading(true)
    try {
      const response = await loginAndAcceptInvite({ token, password: signinPassword })
      onSuccess(response)
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 401) {
          setSigninError('Incorrect password. Please try again.')
        } else if (err.status === 404) {
          setSigninError('No account found for this email.')
          switchTab('register')
        } else {
          setSigninError(err.detail || 'Something went wrong. Please try again.')
        }
      } else {
        setSigninError('Something went wrong. Please try again.')
      }
    } finally {
      setSigninLoading(false)
    }
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()
    setRegisterError(null)
    setRegisterLoading(true)
    try {
      const response = await registerAndAcceptInvite({ token, fullName, password: registerPassword })
      onSuccess(response)
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409) {
          setRegisterError('An account with this email already exists.')
          switchTab('signin')
        } else {
          setRegisterError(err.detail || 'Something went wrong. Please try again.')
        }
      } else {
        setRegisterError('Something went wrong. Please try again.')
      }
    } finally {
      setRegisterLoading(false)
    }
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100">
            <Building2 className="h-5 w-5 text-primary-600" />
          </div>
          <div>
            <CardTitle className="text-lg">{inviteInfo.workspaceName}</CardTitle>
            <CardDescription>Workspace invitation</CardDescription>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        <p className="text-sm text-neutral-600">
          You've been invited to join{' '}
          <span className="font-semibold text-neutral-900">{inviteInfo.workspaceName}</span>.
          {activeTab === 'signin'
            ? ' Sign in to accept.'
            : ' Create an account to accept.'}
        </p>

        {/* Tab toggles */}
        <div className="flex rounded-lg border border-neutral-200 p-1 gap-1">
          <button
            type="button"
            onClick={() => switchTab('signin')}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              activeTab === 'signin'
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            Sign in
          </button>
          <button
            type="button"
            onClick={() => switchTab('register')}
            className={`flex-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
              activeTab === 'register'
                ? 'bg-primary-600 text-white shadow-sm'
                : 'text-neutral-600 hover:text-neutral-900'
            }`}
          >
            Create account
          </button>
        </div>

        {/* ── Sign-in form ─────────────────────────────────────────────────── */}
        {activeTab === 'signin' && (
          <form onSubmit={handleSignin} className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="signin-email">Email</Label>
              <Input
                id="signin-email"
                type="email"
                value={inviteInfo.email}
                readOnly
                className="bg-neutral-50 text-neutral-500 cursor-not-allowed"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="signin-password">Password</Label>
              <div className="relative">
                <Input
                  id="signin-password"
                  type={signinShowPw ? 'text' : 'password'}
                  value={signinPassword}
                  onChange={(e) => setSigninPassword(e.target.value)}
                  placeholder="Your password"
                  required
                  autoFocus
                />
                <button
                  type="button"
                  onClick={() => setSigninShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                  tabIndex={-1}
                >
                  {signinShowPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {signinError && (
              <p className="text-sm text-destructive-600">{signinError}</p>
            )}

            <Button type="submit" className="w-full" disabled={signinLoading}>
              {signinLoading ? 'Signing in…' : 'Sign in and accept'}
            </Button>
          </form>
        )}

        {/* ── Register form ─────────────────────────────────────────────────── */}
        {activeTab === 'register' && (
          <form onSubmit={handleRegister} className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="reg-email">Email</Label>
              <Input
                id="reg-email"
                type="email"
                value={inviteInfo.email}
                readOnly
                className="bg-neutral-50 text-neutral-500 cursor-not-allowed"
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="reg-name">Full name</Label>
              <Input
                id="reg-name"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Your full name"
                required
                autoFocus
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="reg-password">Password</Label>
              <div className="relative">
                <Input
                  id="reg-password"
                  type={registerShowPw ? 'text' : 'password'}
                  value={registerPassword}
                  onChange={(e) => setRegisterPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  minLength={8}
                  required
                />
                <button
                  type="button"
                  onClick={() => setRegisterShowPw((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                  tabIndex={-1}
                >
                  {registerShowPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {registerError && (
              <p className="text-sm text-destructive-600">{registerError}</p>
            )}

            <Button type="submit" className="w-full" disabled={registerLoading}>
              {registerLoading ? 'Creating account…' : 'Create account and accept'}
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Main page component ──────────────────────────────────────────────────────

export function AcceptInvitePage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token') ?? ''

  const [pageState, setPageState] = useState<PageState>('loading')
  const [inviteInfo, setInviteInfo] = useState<InviteInfoResponse | null>(null)
  const [defaultTab] = useState<LoginTab>('signin')

  const { accessToken, email: loggedInEmail, setAccessToken } = useAuthStore()
  const { setWorkspace } = useWorkspaceStore()

  // ── Resolve invite state on mount / whenever auth changes ─────────────────
  useEffect(() => {
    if (!token) {
      setPageState('invalid')
      return
    }

    const init = async () => {
      setPageState('loading')
      try {
        const info = await getInviteInfo(token)
        setInviteInfo(info)

        if (info.status === 'ACCEPTED') { setPageState('already_accepted'); return }
        if (info.status === 'REJECTED') { setPageState('already_rejected'); return }
        if (info.status === 'SUPERSEDED') { setPageState('invalid'); return }
        if (info.expired) { setPageState('expired'); return }

        // Invite is PENDING and valid — check auth
        if (!accessToken) {
          sessionStorage.setItem('returnUrl', `/accept-invite?token=${token}`)
          setPageState('needs_login')
          return
        }

        // Logged in — verify email matches
        if (loggedInEmail && loggedInEmail.toLowerCase() !== info.email.toLowerCase()) {
          setPageState('email_mismatch')
          return
        }

        setPageState('ready')
      } catch (err) {
        setPageState('invalid')
      }
    }

    init()
  }, [token, accessToken, loggedInEmail])

  // ── Invite-auth success handler — shared by both tabs ─────────────────────

  const handleInviteAuthSuccess = (response: AcceptInviteAuthResponse) => {
    setAccessToken(response.accessToken)
    setWorkspace(response.workspaceId, response.workspaceName)
    toast.success('Welcome aboard!', {
      description: `You've joined ${response.workspaceName}.`,
    })
    navigate('/', { replace: true })
  }

  // ── Authenticated accept / decline handlers ────────────────────────────────

  const handleAccept = async () => {
    setPageState('action_loading')
    try {
      await acceptInvite({ token })
      setPageState('accepted')
    } catch {
      toast.error('Failed to accept invite. Please try again.')
      setPageState('ready')
    }
  }

  const handleDecline = async () => {
    setPageState('action_loading')
    try {
      await rejectInvite({ token })
      setPageState('declined')
    } catch {
      toast.error('Failed to decline invite. Please try again.')
      setPageState('ready')
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <PageShell>
      {pageState === 'loading' && (
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-8 text-center">
            <div className="h-10 w-10 animate-pulse rounded-full bg-neutral-200" />
            <div className="space-y-2 w-full max-w-xs">
              <div className="h-4 animate-pulse rounded bg-neutral-100" />
              <div className="h-3 animate-pulse rounded bg-neutral-100 w-3/4 mx-auto" />
            </div>
          </CardContent>
        </Card>
      )}

      {pageState === 'invalid' && (
        <StatusScreen
          icon={<LinkIcon className="h-6 w-6 text-destructive-600" />}
          iconClass="bg-destructive-100"
          title="Invalid invite link"
          description="This invite link is invalid or has already been used."
          action={
            <Link to="/login" className="text-sm text-primary-600 hover:underline">
              Go to login
            </Link>
          }
        />
      )}

      {pageState === 'already_accepted' && (
        <StatusScreen
          icon={<CheckCircle2 className="h-6 w-6 text-success-600" />}
          iconClass="bg-success-100"
          title="Already accepted"
          description="This invite has already been accepted."
          action={
            <Button onClick={() => navigate('/dashboard')} className="mt-2">
              Go to dashboard
            </Button>
          }
        />
      )}

      {pageState === 'already_rejected' && (
        <StatusScreen
          icon={<XCircle className="h-6 w-6 text-neutral-500" />}
          iconClass="bg-neutral-100"
          title="Invite declined"
          description="This invite was previously declined."
          action={
            <Link to="/login" className="text-sm text-primary-600 hover:underline">
              Go to login
            </Link>
          }
        />
      )}

      {pageState === 'expired' && (
        <StatusScreen
          icon={<Clock className="h-6 w-6 text-warning-600" />}
          iconClass="bg-warning-100"
          title="Invite expired"
          description="This invite link has expired. Ask the workspace admin to send a new one."
          action={
            <Link to="/login" className="text-sm text-primary-600 hover:underline">
              Go to login
            </Link>
          }
        />
      )}

      {pageState === 'needs_login' && inviteInfo && (
        <InviteAuthCard
          inviteInfo={inviteInfo}
          token={token}
          defaultTab={defaultTab}
          onSuccess={handleInviteAuthSuccess}
        />
      )}

      {pageState === 'email_mismatch' && inviteInfo && loggedInEmail && (
        <MismatchCard inviteInfo={inviteInfo} loggedInEmail={loggedInEmail} />
      )}

      {(pageState === 'ready' || pageState === 'action_loading') && inviteInfo && (
        <AcceptDeclineCard
          inviteInfo={inviteInfo}
          token={token}
          onAccepted={handleAccept}
          onDeclined={handleDecline}
          isLoading={pageState === 'action_loading'}
        />
      )}

      {pageState === 'accepted' && inviteInfo && (
        <StatusScreen
          icon={<CheckCircle2 className="h-6 w-6 text-success-600" />}
          iconClass="bg-success-100"
          title="Welcome aboard!"
          description={`You've joined ${inviteInfo.workspaceName}. Select the workspace to get started.`}
          action={
            <Button onClick={() => navigate('/workspace-select')} className="mt-2">
              Select workspace
            </Button>
          }
        />
      )}

      {pageState === 'declined' && inviteInfo && (
        <StatusScreen
          icon={<XCircle className="h-6 w-6 text-neutral-500" />}
          iconClass="bg-neutral-100"
          title="Invite declined"
          description={`You've declined the invitation to ${inviteInfo.workspaceName}.`}
          action={
            <Link to="/dashboard" className="text-sm text-primary-600 hover:underline">
              Go to dashboard
            </Link>
          }
        />
      )}
    </PageShell>
  )
}
